import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { serverTypeLabel, serverTypeColor, CREATABLE_SERVER_TYPES } from '@/utils/server-type';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, TestTube, Loader2, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { xaccelService, XaccelServer, getErrorMessage } from '@/services';
import type { ServerType } from '@/services/xaccel.service';

const serverSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  server_url: z.string().url('validation.url'),
  server_token: z.string().min(1, 'validation.required'),
  server_type: z.enum(['xaccel', 'ffstream', 'ssp']),
  is_active: z.boolean(),
});

type ServerForm = z.infer<typeof serverSchema>;


export default function XaccelServers() {
  const { t } = useTranslation();
  const [servers, setServers] = useState<XaccelServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingServer, setEditingServer] = useState<XaccelServer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [showToken, setShowToken] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ServerForm>({
    resolver: zodResolver(serverSchema),
    defaultValues: {
      server_type: 'xaccel',
      is_active: true,
    },
  });

  const watchedType = watch('server_type');

  const fetchServers = async () => {
    try {
      const data = await xaccelService.list();
      setServers(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const openCreateModal = () => {
    setEditingServer(null);
    setShowToken(false);
    reset({
      name: '',
      server_url: '',
      server_token: '',
      server_type: 'xaccel',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (server: XaccelServer) => {
    setEditingServer(server);
    setShowToken(false);
    reset({
      name: server.name,
      server_url: server.server_url,
      server_token: server.server_token || '',
      server_type: server.server_type || 'xaccel',
      is_active: server.is_active === 1,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: ServerForm) => {
    try {
      const payload = {
        name: data.name,
        server_url: data.server_url,
        server_token: data.server_token,
        server_type: data.server_type,
        is_active: data.is_active,
      };

      if (editingServer) {
        await xaccelService.update(editingServer.id, payload);
        toast.success(t('common.updated'));
      } else {
        await xaccelService.create(payload);
        toast.success(t('common.created'));
      }

      setShowModal(false);
      fetchServers();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await xaccelService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchServers();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    try {
      const result = await xaccelService.test(id);
      if (result.working) {
        toast.success(t('xaccel.testSuccess'));
      } else {
        toast.error(`${t('xaccel.testFailed')}: ${result.error}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('xaccel.testFailed'));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('streaming.title')}</h1>
          <p className="text-dark-400 mt-1">
            {t('streaming.configuredCount', { count: servers.length })}
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('streaming.add')}
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : servers.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('xaccel.name')}</th>
                <th>{t('streaming.type')}</th>
                <th>{t('xaccel.serverUrl')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {servers.map((server) => {
                const stype = (server.server_type || 'xaccel') as ServerType;
                return (
                  <tr key={server.id}>
                    <td className="font-medium text-dark-100">{server.name}</td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${serverTypeColor(stype)}`}>
                        {serverTypeLabel(stype)}
                      </span>
                    </td>
                    <td className="text-dark-300 text-sm truncate max-w-xs">{server.server_url}</td>
                    <td>
                      <span
                        className={server.is_active ? 'badge-success' : 'badge-error'}
                      >
                        {server.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td>
                      {deleteConfirm === server.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(server.id)}
                            className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleTest(server.id)}
                            disabled={testingId === server.id}
                            className="p-1.5 rounded-lg hover:bg-primary-50 text-dark-400 hover:text-primary-600"
                            title={t('xaccel.test')}
                          >
                            {testingId === server.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <TestTube size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => openEditModal(server)}
                            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(server.id)}
                            className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-lg m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingServer ? editingServer.name : t('streaming.add')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Server Type Radio */}
              <div>
                <label className="label">{t('streaming.serverType')}</label>
                <Controller
                  name="server_type"
                  control={control}
                  render={({ field }) => (
                    <div className="flex gap-3">
                      {([...CREATABLE_SERVER_TYPES, ...(field.value === 'ffstream' ? ['ffstream' as const] : [])]).map((type) => (
                        <label
                          key={type}
                          className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            field.value === type
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-dark-700 hover:border-dark-500'
                          }`}
                        >
                          <input
                            type="radio"
                            className="sr-only"
                            checked={field.value === type}
                            onChange={() => field.onChange(type)}
                          />
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            field.value === type ? 'border-primary-600' : 'border-dark-500'
                          }`}>
                            {field.value === type && (
                              <div className="w-2 h-2 rounded-full bg-primary-600" />
                            )}
                          </div>
                          <div>
                            <span className="font-medium text-dark-100 text-sm">{serverTypeLabel(type)}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                />
              </div>

              <div>
                <label className="label">{t('streaming.serverName')} *</label>
                <input
                  type="text"
                  className={errors.name ? 'input-error' : 'input'}
                  placeholder="Server 1"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.name.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">{t('streaming.serverUrl')} *</label>
                <input
                  type="url"
                  className={errors.server_url ? 'input-error' : 'input'}
                  placeholder={watchedType === 'ffstream' ? 'https://ffstream.example.com' : watchedType === 'ssp' ? 'https://panel.example.com (SSP)' : 'https://xaccel.example.com'}
                  {...register('server_url')}
                />
                {errors.server_url && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.server_url.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">
                  {watchedType === 'ffstream' || watchedType === 'ssp' ? t('streaming.apiKey') : t('xaccel.serverToken')} *
                </label>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    className={`${errors.server_token ? 'input-error' : 'input'} pr-10`}
                    placeholder={editingServer ? t('streaming.reEnterToken') : ''}
                    {...register('server_token')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200"
                  >
                    {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.server_token && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.server_token.message || '')}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                  {...register('is_active')}
                />
                <label htmlFor="is_active" className="text-sm text-dark-200">
                  {t('common.active')}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
